import PropTypes from 'prop-types';
import {
  AdornedInput,
  Box,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Label,
  Paragraph,
  Text,
  Tooltip,
} from '@zillow/constellation-10';
import { IconSparkleOutline } from '@zillow/constellation-icons';

import RadioButton from '../components/RadioButton';
import SecurityDepositClaimExample from '../components/SecurityDepositClaimExample';

import HomeSparkle from './components/HomeSparkle';

import numberUtils from '../../../utils/numberUtils';
import { REQUIRE_SECURITY_DEPOSIT, WAIVE_SECURITY_DEPOSIT } from './constants';

const propTypes = {
  securityDepositSelection: PropTypes.string,
  securityDepositInputValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setSecurityDepositOptions: PropTypes.func.isRequired,
  securityDepositInputHasError: PropTypes.bool,
  securityDepositCurrentProperty: PropTypes.number,
  isForManagePolicyPageV2: PropTypes.bool,
};
const defaultProps = {};

const TwoStepOptInReviewSecurityDeposit = ({
  securityDepositSelection,
  securityDepositInputValue,
  setSecurityDepositOptions,
  securityDepositInputHasError,
  securityDepositCurrentProperty,
  isForManagePolicyPageV2,
}) => {
  const handleWaiveSecurityDepositClick = () => {
    setSecurityDepositOptions(WAIVE_SECURITY_DEPOSIT);
  };

  const handleRequireSecurityDepositClick = () => {
    setSecurityDepositOptions(REQUIRE_SECURITY_DEPOSIT);

    setTimeout(() => {
      const element = document.getElementById('security-deposit');
      if (element != null && 'scrollIntoView' in element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  const onSecurityDepositAmountChange = (event) => {
    const { value } = event.target;
    if (numberUtils.isOnlyIntegers(value) || value === '') {
      setSecurityDepositOptions(REQUIRE_SECURITY_DEPOSIT, value);
    }
  };

  const renderRadioExampleText = (isWaived) => {
    return <SecurityDepositClaimExample isWaived={isWaived} securityDepositInputValue={securityDepositInputValue} />;
  };

  return (
    <Box
      css={{ padding: { base: '200', md: '{spacing.800} {spacing.600}' } }}
      data-testid="TwoStepOptInReviewSecurityDeposit"
    >
      {!isForManagePolicyPageV2 && (
        <div>
          <Box css={{ display: 'flex', flexDirection: 'column', marginBottom: '200' }}>
            <HomeSparkle size="lg" marginBottom="sm" />
            <Heading level={4} fontFamily="heading-alt">
              You&apos;re one step closer to Rental Protection
            </Heading>
          </Box>
          <Paragraph css={{ marginBottom: '400' }}>
            Sit back and wait for applications! Once you find a great fit, you&apos;ll review and verify their income
            documents.
          </Paragraph>
          <Divider css={{ marginBlock: '800' }} />
        </div>
      )}
      <Paragraph css={{ textStyle: isForManagePolicyPageV2 ? 'heading-sm' : 'heading-xs', marginBottom: '400' }}>
        Review security deposit options
      </Paragraph>
      {isForManagePolicyPageV2 ? (
        <Box
          css={{ display: 'flex', flexDirection: 'column', marginBottom: '400' }}
          data-testid="TwoStepOptInReviewSecurityDeposit-Description"
        >
          <Paragraph css={{ marginBottom: '400' }}>
            Rental Protection provides up to $5,000 of coverage per policy. If you choose to require a deposit, covered
            claims will be reduced by the amount of any security deposit you collect.
          </Paragraph>
          <div>
            <Icon
              render={<IconSparkleOutline />}
              size="md"
              css={{ color: 'icon.accent.brand.hero', marginRight: '200' }}
            />
            <Text>To avoid the hassle of managing a security deposit, consider waiving your security deposit.</Text>
          </div>
        </Box>
      ) : (
        <Paragraph css={{ marginBottom: '400' }}>
          If you choose to require a security deposit, you&apos;ll still have up to $5,000 of coverage, but the amount
          of your claim will be reduced by {numberUtils.asCurrency(securityDepositCurrentProperty, '$0,0')} (your
          current deposit amount).{' '}
          <Tooltip.Root>
            <Tooltip.Trigger />
            <Tooltip.Portal>
              <Tooltip.Content>
                <div>
                  <Paragraph css={{ marginBottom: '400' }}>
                    For example, if a landlord collects a $1,000 deposit, they still have $5,000 worth of coverage. If a
                    renter then causes $4,500 in covered damage, Rental Protection will cover up to $3,500 of the claim.
                  </Paragraph>
                  <Paragraph>Covered Loss ($4,500) - Deposit Amount ($1,000) = Loss Payment (up to $3,500) </Paragraph>
                </div>
                <Tooltip.Arrow />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Paragraph>
      )}
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          marginTop: '800',
          gap: { base: '200', md: '400' },
        }}
      >
        <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 6' } }}>
          <RadioButton
            onClick={handleRequireSecurityDepositClick}
            name="security-deposit-choice"
            id="TwoStepOptInReviewSecurityDeposit-RequireSecurityDeposit-RadioBtn"
            label="Require a deposit"
            checked={securityDepositSelection === REQUIRE_SECURITY_DEPOSIT}
            radioExampleText={isForManagePolicyPageV2 && renderRadioExampleText()}
            data-testid="TwoStepOptInReviewSecurityDeposit-RequireSecurityDeposit-RadioBtn"
          />
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 6' } }}>
          <RadioButton
            onClick={handleWaiveSecurityDepositClick}
            name="security-deposit-choice"
            id="TwoStepOptInReviewSecurityDeposit-WaiveSecurityDeposit-RadioBtn"
            label="Waive deposit"
            checked={securityDepositSelection === WAIVE_SECURITY_DEPOSIT}
            radioExampleText={isForManagePolicyPageV2 && renderRadioExampleText(true)}
            data-testid="TwoStepOptInReviewSecurityDeposit-WaiveSecurityDeposit-RadioBtn"
          />
        </Box>
      </Box>
      {securityDepositSelection === REQUIRE_SECURITY_DEPOSIT && (
        <Box
          id="security-deposit"
          css={{ marginTop: '600' }}
          data-testid="TwoStepOptInReviewSecurityDeposit-RequireSecurityDeposit-UpdateInfo"
        >
          <Paragraph textStyle="body-bold">Do you want to lower your security deposit amount?</Paragraph>
          <Paragraph css={{ marginBottom: '600' }}>We&apos;ll update your listing with the new amount.</Paragraph>
          <FormField
            label={<Label>Security deposit</Label>}
            control={
              <AdornedInput
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                input={
                  <AdornedInput.Input
                    aria-label="Security deposit"
                    value={securityDepositInputValue}
                    onChange={onSecurityDepositAmountChange}
                    data-test="security-deposit-input"
                  />
                }
              />
            }
            description={
              securityDepositInputHasError && (
                <FormHelp error={true}>Amount should generally not exceed 150% of the base rent</FormHelp>
              )
            }
          />
        </Box>
      )}
    </Box>
  );
};

TwoStepOptInReviewSecurityDeposit.propTypes = propTypes;
TwoStepOptInReviewSecurityDeposit.defaultProps = defaultProps;

export default TwoStepOptInReviewSecurityDeposit;
